import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { App, Button, Form, Input, Spin } from 'antd';
import { PhoneOutlined, MailOutlined } from '@ant-design/icons';
import axios from "axios";
import "./ContactScreen.css";

const contactMail = "contact";
const contactPhone = "01" + "84808041";

const TABS = [
  {
    key: 'contact',
    label: 'Coordonnées',
    type: 'info',
  },
  {
    key: 'info',
    label: "Demande d'informations",
    type: 'form',
    defaultMessage: "Bonjour,\n\nJe souhaite obtenir des informations supplémentaires sur vos outils.\n\nCordialement,",
  },
  {
    key: 'demo',
    label: 'Demande de démonstration',
    type: 'form',
    defaultMessage: "Bonjour,\n\nJ'aimerais avoir une démonstration de vos outils.\n\nCordialement,",
  },
  {
    key: 'test',
    label: 'Demander un compte de test gratuit',
    type: 'form',
    defaultMessage: "Bonjour,\n\nJe souhaiterais obtenir un compte pour tester vos outils.\n\nRôle: MEDECIN / INFIRMIER / ASSISTANT_MEDICAL / PREVENTEUR / GESTIONNAIRE_ADMINISTRATIF (indiquer votre rôle)\n\nCordialement,",
  },
];

function ContactScreen() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('contact');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();

  useEffect(() => {
    // Titre/description propres à la page pour le SEO (sinon Google retombe
    // sur le titre par défaut d'index.html et improvise le libellé du sitelink).
    const previousTitle = document.title;
    const descEl = document.querySelector('meta[name="description"]');
    const previousDescription = descEl ? descEl.getAttribute("content") : null;
    document.title = "Contact | RAVAIL";
    if (descEl) descEl.setAttribute("content", "Contactez l'équipe RAVAIL : questions, demandes de démonstration et accompagnement autour de la santé au travail.");

    return () => {
      document.title = previousTitle;
      if (descEl && previousDescription !== null) descEl.setAttribute("content", previousDescription);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      const found = TABS.find(t => t.key === tab);
      if (found) handleTabClick(found);
    }
    if (location.state?.email) {
      setTimeout(() => form.setFieldValue('email', location.state.email), 0);
    }
  }, [location.search]);

  const handleTabClick = (tab) => {
    setActiveTab(tab.key);
    if (tab.type === 'form') {
      form.setFieldValue('message', tab.defaultMessage);
    }
  };

  const onFinish = (values) => {
    setLoading(true);
    axios.post('/api/contact', values).then((data) => {
      setLoading(false);
      if (data.status === 200) {
        message.success("Votre message a bien été envoyé, nous y répondrons dans les plus brefs délais");
        form.resetFields();
        const currentTab = TABS.find(t => t.key === activeTab);
        if (currentTab?.defaultMessage) {
          form.setFieldValue('message', currentTab.defaultMessage);
        }
      } else {
        message.error("Une erreur est survenue lors de l'envoi de votre message, veuillez réessayer");
      }
    }).catch((err) => {
      setLoading(false);
      message.error("Une erreur est survenue lors de l'envoi de votre message : " + (err?.response?.data?.message ? err.response.data.message : (err?.response?.data ? err.response.data : err.message)));
    });
  };

  const currentTab = TABS.find(t => t.key === activeTab);

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="contact-inner">
          <h1 className="contact-hero-title">Contactez-nous</h1>
          <p className="contact-hero-subtitle">Notre équipe vous répond dans les plus brefs délais.</p>
        </div>
      </div>
      <div className="wave-contact-to-content" aria-hidden="true">
        <svg viewBox="0 0 1280 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120 H1280 V60 C1000 0 300 120 0 40 Z" fill="#fefefe"/>
        </svg>
      </div>
      <section className="contact-section">
      <div className="contact-inner">
        <div className="contact-layout">

          {/* Colonne gauche — navigation */}
          <nav className="contact-nav">
            {TABS.map(tab => (
              <button
                key={tab.key}
                className={`contact-nav-btn${activeTab === tab.key ? ' contact-nav-btn--active' : ''}`}
                onClick={() => handleTabClick(tab)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Colonne droite — contenu */}
          <div className="contact-content">

            {currentTab?.type === 'info' && (
              <div className="contact-info">
                <h2 className="contact-info-title">Coordonnées</h2>
                <p className="contact-info-subtitle">Notre équipe est disponible pour répondre à toutes vos questions.</p>
                <div className="contact-info-items">
                  <a
                    className="contact-info-item"
                    href={`tel:${contactPhone}`}
                  >
                    <PhoneOutlined className="contact-info-icon" />
                    <div>
                      <span className="contact-info-label">Téléphone</span>
                      <span className="contact-info-value">{contactPhone.replace(/(\d{2})(?=\d)/g, '$1 ')}</span>
                    </div>
                  </a>
                  <a
                    className="contact-info-item"
                    href={`mailto:${contactMail}@ravail.fr`}
                  >
                    <MailOutlined className="contact-info-icon" />
                    <div>
                      <span className="contact-info-label">Email</span>
                      <span className="contact-info-value">{contactMail}@ravail.fr</span>
                    </div>
                  </a>
                </div>
              </div>
            )}

            {currentTab?.type === 'form' && (
              <div className="contact-form-wrap">
                <h2 className="contact-form-title">{currentTab.label}</h2>
                <Spin spinning={loading}>
                  <Form
                    form={form}
                    layout="vertical"
                    name="contact"
                    onFinish={onFinish}
                    scrollToFirstError
                    initialValues={{ message: currentTab.defaultMessage }}
                  >
                    <div className="contact-form-row">
                      <Form.Item
                        name="name"
                        label="Nom et Prénom(s)"
                        rules={[{ required: true, message: 'Veuillez entrer votre nom et prénom(s)', whitespace: true }]}
                      >
                        <Input size="large" placeholder="Marie Dupont" />
                      </Form.Item>
                      <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                          { type: 'email', message: 'Adresse e-mail invalide' },
                          { required: true, message: 'Veuillez entrer votre adresse e-mail' },
                        ]}
                      >
                        <Input size="large" placeholder="marie.dupont@exemple.fr" />
                      </Form.Item>
                    </div>
                    <div className="contact-form-row">
                      <Form.Item name="service" label="Service / Entreprise / Organisme">
                        <Input size="large" placeholder="Optionnel" />
                      </Form.Item>
                      <Form.Item name="phone" label="Numéro de téléphone">
                        <Input size="large" placeholder="Optionnel" />
                      </Form.Item>
                    </div>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) => prev.phone !== curr.phone || prev.name !== curr.name}
                    >
                      {({ getFieldValue, setFieldValue }) => {
                        const phone = getFieldValue('phone');
                        if (!phone) return null;
                        return (
                          <div className="contact-callback-hint">
                            <span className="contact-callback-label">Vous avez renseigné un numéro —</span>
                            <button
                              type="button"
                              className="contact-callback-btn"
                              onClick={() => {
                                const name = getFieldValue('name') || '';
                                setFieldValue('message', `Bonjour,\n\nJe souhaiterais être rappelé(e) au ${phone}.\n\nCordialement,\n${name}`);
                              }}
                            >
                              Rappelez-moi
                            </button>
                          </div>
                        );
                      }}
                    </Form.Item>
                    <Form.Item
                      name="message"
                      label="Message"
                      rules={[{ required: true, message: 'Veuillez entrer votre message' }]}
                    >
                      <Input.TextArea autoSize={{ minRows: 6 }} />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        className="contact-submit-btn"
                        size="large"
                      >
                        Envoyer votre message
                      </Button>
                    </Form.Item>
                  </Form>
                </Spin>
              </div>
            )}

          </div>
        </div>
      </div>
    </section>
    </div>
  );
}

export default ContactScreen;
